import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { isTaskMantisDesktop } from "../desktopUpdate";
import {
  CAPTURE_HOTKEY_LABEL,
  markCaptureHotkeyTipSeen,
  shouldShowCaptureSpotlight,
} from "./shared/captureHotkeyTip";
import { normalizeCaptureHotkeyState } from "./shared/useCaptureHotkeyState";
import "./CaptureHotkeyTip.css";

const SHOW_DELAY_MS = 1500;

/**
 * Desktop-only spotlight for the Ctrl+Option+K global capture hotkey.
 *
 * On a fresh install quick capture is off, so this is the opt-in prompt:
 * "Enable capture" is what turns the hotkey on and triggers the macOS
 * Accessibility permission dialog. Once enabled it reverts to an awareness
 * tip, shown again after each upgrade (version-gated) and never re-shown to
 * someone who deliberately said no.
 *
 * @example
 * <CaptureHotkeyTip />
 * // Renders nothing in the browser or after dismissal for the current version
 */
const CaptureHotkeyTip = () => {
  const [visible, setVisible] = useState(false);
  const [appVersion, setAppVersion] = useState("");
  const [captureState, setCaptureState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [permissionPending, setPermissionPending] = useState(false);

  useEffect(() => {
    if (!isTaskMantisDesktop()) return undefined;

    let cancelled = false;
    const evaluate = async () => {
      let version = "";
      try {
        version = String((await window.starmeshDesktop?.getVersion?.()) || "").trim();
      } catch (_) {
        // Version lookup failed; fall through and still decide on capture state.
      }
      if (cancelled) return;
      setAppVersion(version);

      let state = null;
      try {
        state = normalizeCaptureHotkeyState(
          await window.starmeshDesktop?.getCaptureHotkeyState?.()
        );
      } catch (_) {
        state = null;
      }
      if (cancelled) return;
      setCaptureState(state);

      if (!shouldShowCaptureSpotlight({ captureState: state, version })) return;

      setTimeout(() => {
        if (!cancelled) setVisible(true);
      }, SHOW_DELAY_MS);
    };

    void evaluate();
    return () => {
      cancelled = true;
    };
  }, []);

  const close = useCallback(() => {
    markCaptureHotkeyTipSeen(appVersion);
    setVisible(false);
  }, [appVersion]);

  /** "Not now" / × — record the decision so a fresh install isn't asked again. */
  const handleDecline = useCallback(() => {
    if (captureState && !captureState.decided) {
      void window.starmeshDesktop?.setCaptureHotkeyEnabled?.(false)?.catch?.(() => {});
    }
    close();
  }, [captureState, close]);

  const handleEnable = useCallback(async () => {
    setBusy(true);
    try {
      const next = normalizeCaptureHotkeyState(
        await window.starmeshDesktop?.setCaptureHotkeyEnabled?.(true)
      );
      setCaptureState(next);
      // The macOS dialog is modal to the system, not to us: if permission
      // isn't granted by the time it returns, keep the card up with the
      // follow-up step instead of closing on a shortcut that can't fire yet.
      if (!next.accessibilityTrusted) {
        setPermissionPending(true);
        return;
      }
      close();
    } finally {
      setBusy(false);
    }
  }, [close]);

  useEffect(() => {
    if (!visible) return undefined;
    const handleEscape = (event) => {
      if (event.key === "Escape") handleDecline();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [handleDecline, visible]);

  if (!visible) return null;

  const needsOptIn = Boolean(captureState) && !captureState.enabled;

  return createPortal(
    <div className="capture-hotkey-tip-region" aria-live="polite">
      <div
        className="capture-hotkey-tip"
        role="status"
        aria-labelledby="capture-hotkey-tip-title"
        aria-describedby="capture-hotkey-tip-desc"
      >
        <button
          type="button"
          className="capture-hotkey-tip__close"
          onClick={handleDecline}
          aria-label="Dismiss"
        >
          ×
        </button>
        <span className="capture-hotkey-tip__badge">New</span>
        <p id="capture-hotkey-tip-title" className="capture-hotkey-tip__title">
          Capture a task from anywhere
        </p>
        <p id="capture-hotkey-tip-desc" className="capture-hotkey-tip__desc">
          {permissionPending
            ? `Almost there — macOS needs Accessibility permission before ${CAPTURE_HOTKEY_LABEL} can read your selection. Turn on Task Mantis under Privacy & Security → Accessibility.`
            : "Select text in Slack, Gmail, or any app, then press the shortcut below to add it as a Task Mantis task."}
        </p>
        <div className="capture-hotkey-tip__kbd-row">
          {CAPTURE_HOTKEY_LABEL.split("+").map((part) => (
            <kbd className="capture-hotkey-tip__kbd" key={part}>
              {part}
            </kbd>
          ))}
        </div>
        {permissionPending ? (
          <>
            <button
              type="button"
              className="capture-hotkey-tip__primary"
              onClick={() => window.starmeshDesktop?.openAccessibilitySettings?.()}
            >
              Open System Settings
            </button>
            <button type="button" className="capture-hotkey-tip__secondary" onClick={close}>
              I'll do it later
            </button>
          </>
        ) : needsOptIn ? (
          <>
            <button
              type="button"
              className="capture-hotkey-tip__primary"
              onClick={() => void handleEnable()}
              disabled={busy}
            >
              {busy ? "Enabling…" : "Enable capture"}
            </button>
            <button
              type="button"
              className="capture-hotkey-tip__secondary"
              onClick={handleDecline}
            >
              Not now
            </button>
          </>
        ) : (
          <button type="button" className="capture-hotkey-tip__primary" onClick={close}>
            Got it
          </button>
        )}
      </div>
    </div>,
    document.body
  );
};

export default CaptureHotkeyTip;
